// Yatra Child Categories - 3rd level under SubCategory
// Path: src/pages/yatra/childCategory/YatraChildCategories.js
import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, CircularProgress, Snackbar, Alert
} from '@mui/material';
import { Add, Edit, Delete, CloudUpload } from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'https://api.bharatdarshan.app/api';
const UPLOAD_API = 'https://api.bharatdarshan.app/api/vendor/upload-image';
const ASSET_BASE = 'https://api.bharatdarshan.app/';

const YatraChildCategories = () => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('image', file);
    setUploading(true);
    try {
      const r = await axios.post(UPLOAD_API, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (r.data && r.data.success && r.data.imageUrl) setForm(f => ({ ...f, image: r.data.imageUrl }));
      else showSnack('Upload failed', 'error');
    } catch (err) { showSnack('Upload error: ' + (err.response?.data?.message || err.message), 'error'); }
    setUploading(false);
    e.target.value = '';
  };
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);
  const [filterSubId, setFilterSubId] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const loadCategories = async () => {
    try { const r = await axios.get(`${API}/admin/getCategories`); const all = r.data.results || r.data.data || []; setCategories(all.filter(c => c.redirect_page === 'Yatra')); }
    catch (e) { showSnack('Failed to load categories', 'error'); }
  };
  const loadSubCategories = async () => {
    try { const r = await axios.get(`${API}/admin/getSubCategories`); setSubCategories(r.data.results || r.data.data || []); }
    catch (e) { showSnack('Failed to load subcategories', 'error'); }
  };
  const loadChildCategories = async (subId = '') => {
    try {
      const url = subId ? `${API}/admin/getChildCategories?subCategoryId=${subId}` : `${API}/admin/getChildCategories`;
      const r = await axios.get(url); setChildCategories(r.data.data || []);
    } catch (e) { showSnack('Failed to load child categories', 'error'); }
  };

  useEffect(() => { loadCategories(); loadSubCategories(); loadChildCategories(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ categoryId: '', subCategoryId: filterSubId || '', child_category_name: '', image: '', description: '' });
    setDialogOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setForm({
      id: item._id,
      categoryId: item.categoryId?._id || item.categoryId,
      subCategoryId: item.subCategoryId?._id || item.subCategoryId,
      child_category_name: item.child_category_name,
      image: item.image || '',
      description: item.description || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (editing) await axios.post(`${API}/admin/updateChildCategory`, form);
      else await axios.post(`${API}/admin/createChildCategory`, form);
      showSnack(editing ? 'Updated' : 'Created');
      setDialogOpen(false); loadChildCategories(filterSubId);
    } catch (e) { showSnack(e.response?.data?.message || 'Operation failed', 'error'); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await axios.post(`${API}/admin/deleteChildCategory`, { id }); showSnack('Deleted'); loadChildCategories(filterSubId); }
    catch (e) { showSnack('Delete failed', 'error'); }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 3, color: '#FF6B35' }}>Yatra Child Categories</Typography>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <TextField select size="small" label="Filter by Sub Category" value={filterSubId} onChange={(e) => { setFilterSubId(e.target.value); loadChildCategories(e.target.value); }} sx={{ minWidth: 280 }}>
              <MenuItem value="">All</MenuItem>
              {subCategories.map(s => <MenuItem key={s._id} value={s._id}>{s.sub_category_name} {s.categoryId?.category_name ? `(${s.categoryId.category_name})` : ''}</MenuItem>)}
            </TextField>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" sx={{ bgcolor: '#FF6B35' }} startIcon={<Add />} onClick={openCreate}>Add Child Category</Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead><TableRow>
                <TableCell>Image</TableCell><TableCell>Name</TableCell><TableCell>Parent Category</TableCell>
                <TableCell>Sub Category</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {childCategories.map(c => (
                  <TableRow key={c._id}>
                    <TableCell>{c.image ? <img src={c.image.startsWith('http') ? c.image : `https://api.bharatdarshan.app/${c.image}`} alt="" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }} /> : '—'}</TableCell>
                    <TableCell>{c.child_category_name}</TableCell>
                    <TableCell>{c.categoryId?.category_name || '—'}</TableCell>
                    <TableCell>{c.subCategoryId?.sub_category_name || '—'}</TableCell>
                    <TableCell><Chip label={c.status} color={c.status === 'Active' ? 'success' : 'default'} size="small" /></TableCell>
                    <TableCell align="right">
                      <IconButton color="primary" onClick={() => openEdit(c)}><Edit /></IconButton>
                      <IconButton color="error" onClick={() => handleDelete(c._id)}><Delete /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {childCategories.length === 0 && <TableRow><TableCell colSpan={6} align="center">No child categories</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit' : 'Add'} Child Category</DialogTitle>
        <DialogContent>
          <TextField select fullWidth label="Parent Category" value={form.categoryId || ''} onChange={(e) => setForm({ ...form, categoryId: e.target.value, subCategoryId: '' })} margin="normal">
            {categories.map(c => <MenuItem key={c._id} value={c._id}>{c.category_name}</MenuItem>)}
          </TextField>
          <TextField select fullWidth label="Sub Category" value={form.subCategoryId || ''} onChange={(e) => setForm({ ...form, subCategoryId: e.target.value })} margin="normal" disabled={!form.categoryId}>
            {subCategories.filter(s => !form.categoryId || (s.categoryId?._id || s.categoryId) === form.categoryId).map(s => <MenuItem key={s._id} value={s._id}>{s.sub_category_name}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="Child Category Name" value={form.child_category_name || ''} onChange={(e) => setForm({ ...form, child_category_name: e.target.value })} margin="normal" />
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="caption" color="textSecondary">Image (upload or paste URL)</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
              <Button variant="outlined" component="label" size="small" disabled={uploading}>{uploading ? 'Uploading...' : 'Upload Image'}<input hidden type="file" accept="image/*" onChange={handleUpload} /></Button>
              {form.image ? <img src={form.image.startsWith('http') ? form.image : ASSET_BASE + form.image} alt="" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }} /> : null}
            </Box>
          </Box>
          <TextField fullWidth label="Image URL or path" value={form.image || ''} onChange={(e) => setForm({ ...form, image: e.target.value })} margin="normal" />
          <TextField fullWidth label="Description" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} margin="normal" multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>{loading ? <CircularProgress size={20} /> : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.severity}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default YatraChildCategories;
